import { SelectInput, Switch, TextInput } from '@my/ui';
import { Check, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AdrPicker, EwcCodeList } from './CataloguePickers';
import {
  GRADE_VALUES,
  needsTechnicalName,
  validateDraft,
  type ProductDraft,
} from './lib/productDraft';

export interface ProductEditorProps {
  draft: ProductDraft;
  onChange: (next: ProductDraft) => void;
  /** Show validation messages only after the first save attempt. */
  submitted: boolean;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isBusy: boolean;
}

/**
 * The in-row editor for one product: its own details on top, then the two
 * regulatory panels side by side — ADR and waste are independent, and a
 * product can carry both.
 */
export function ProductEditor({
  draft,
  onChange,
  submitted,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: ProductEditorProps) {
  const { t } = useTranslation('configuration');
  const set = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) =>
    onChange({ ...draft, [key]: value });
  const errs = validateDraft(draft);
  const technicalNameNeeded = needsTechnicalName(draft.adr);

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <TextInput
          label={t('products.fields.name')}
          name="name"
          autoComplete="off"
          value={draft.name}
          maxLength={200}
          onChange={(e) => set('name', e.target.value)}
          error={submitted && errs.name}
          errorMessage={t('products.fields.nameRequired')}
        />
        <TextInput
          label={t('products.fields.code')}
          name="code"
          autoComplete="off"
          value={draft.code}
          maxLength={32}
          placeholder={t('products.fields.codePlaceholder')}
          onChange={(e) => set('code', e.target.value)}
        />
        <SelectInput
          label={t('products.fields.grade')}
          name="grade"
          value={draft.grade}
          onChange={(e) => set('grade', e.target.value as ProductDraft['grade'])}
          options={GRADE_VALUES.map((g) => ({ value: g, label: t(`products.grade.${g}`) }))}
        />
        <TextInput
          label={t('products.fields.density')}
          name="density"
          autoComplete="off"
          inputMode="decimal"
          value={draft.density}
          onChange={(e) => set('density', e.target.value)}
          error={submitted && errs.density}
          errorMessage={t('products.fields.densityInvalid')}
        />
        <Switch
          label={t('products.fields.status')}
          name="status"
          checked={draft.status === 'Active'}
          enabledLabel={t('products.fields.statusActive')}
          disabledLabel={t('products.fields.statusInactive')}
          onChange={(e) => set('status', e.currentTarget.checked ? 'Active' : 'Inactive')}
        />
        <div className="md:col-span-3">
          <TextInput
            label={t('products.fields.notes')}
            name="notes"
            autoComplete="off"
            value={draft.notes}
            maxLength={1000}
            onChange={(e) => set('notes', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <RegulationPanel title={t('products.adr.title')} hint={t('products.adr.hint')}>
          <AdrPicker
            value={draft.adr}
            onChange={(adr) =>
              onChange({
                ...draft,
                adr,
                // A starting point for the trade name, never an overwrite: Name
                // is the organisation's own word for it, and many products share
                // one UN entry (UN 1993 alone covers hundreds).
                name: adr && !draft.name.trim() ? adr.name : draft.name,
                // Clearing the row clears its technical name too, as the server does.
                adrTechnicalName: adr ? draft.adrTechnicalName : '',
              })
            }
            disabled={isBusy}
          />
          {/* Only where ADR asks for one (special provision 274, mostly the
              N.O.S. entries) — or where one is already filled in, so a value
              is never hidden while it is still being saved. */}
          {draft.adr && (technicalNameNeeded || draft.adrTechnicalName.trim() !== '') && (
            <TextInput
              label={t('products.adr.technicalName')}
              name="adrTechnicalName"
              autoComplete="off"
              value={draft.adrTechnicalName}
              maxLength={200}
              placeholder={t('products.adr.technicalNamePlaceholder')}
              onChange={(e) => set('adrTechnicalName', e.target.value)}
              // Advisory, not blocking: ADR wants it on the transport document,
              // but the product can be saved and the name added later.
              helperText={technicalNameNeeded ? t('products.adr.technicalNameRequired') : undefined}
              className={
                technicalNameNeeded && !draft.adrTechnicalName.trim()
                  ? 'border-amber-400'
                  : undefined
              }
            />
          )}
          {!draft.adr && (
            <p className="text-[11px] italic text-slate-400">{t('products.adr.none')}</p>
          )}
        </RegulationPanel>

        <RegulationPanel title={t('products.ewc.title')} hint={t('products.ewc.hint')}>
          <EwcCodeList
            value={draft.ewc}
            defaultCode={draft.ewcDefault}
            onChange={(ewc, ewcDefault) =>
              onChange({
                ...draft,
                ewc,
                ewcDefault,
                // Same rule as ADR: the first code fills an empty name, never
                // replaces one.
                name: ewc[0] && !draft.name.trim() ? ewc[0].description : draft.name,
              })
            }
            disabled={isBusy}
          />
          {draft.ewc.length === 0 && (
            <p className="text-[11px] italic text-slate-400">{t('products.ewc.none')}</p>
          )}
        </RegulationPanel>
      </div>

      <div className="flex items-center justify-between gap-1">
        <div>
          {onDelete && (
            <button
              type="button"
              aria-label={t('products.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('products.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('products.ariaSave')}
            onClick={onSave}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function RegulationPanel({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    // The pickers are floating-label inputs: the label sits ~8px ABOVE the box
    // on a white background, so the gap under the hint has to clear it or the
    // label paints over the hint's last line.
    <div className="rounded-sm border border-slate-200 bg-white p-3">
      <div className="mb-5">
        <h3 className="text-xs font-semibold text-slate-800">{title}</h3>
        <p className="mb-4 mt-0.5 text-[11px] text-slate-500">{hint}</p>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
